import React, { useState, useEffect, useRef } from 'react';
import './UploadTest.css';
import { S3Client, PutObjectCommand, GetObjectCommand } from "@aws-sdk/client-s3";
import { CognitoIdentityClient, GetIdCommand, GetCredentialsForIdentityCommand } from "@aws-sdk/client-cognito-identity";
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import threatforgeLogo from '../assets/threatforge-logo.png';
import { FaUpload, FaDownload /* other imports */ } from 'react-icons/fa';
import { 
    CreateMultipartUploadCommand, 
    UploadPartCommand, 
    CompleteMultipartUploadCommand, 
    AbortMultipartUploadCommand 
} from "@aws-sdk/client-s3";




// DO NOT MODIFY THIS - Working Authentication Config
const AUTH_CONFIG = {
    cognitoDomain: 'ai-threatmodel.auth.us-west-2.amazoncognito.com',
    clientId: '3kjqjkmebaovumf6flj3theidc',
    redirectUri: 'https://d1sl47crwy6brf.cloudfront.net',
    identityProvider: 'Federate-OIDC'
};

// AWS Configuration for Identity Pool
const AWS_CONFIG = {
    region: 'us-west-2',
    identityPoolId: 'us-west-2:0fc48765-73a4-434c-ac99-197f0f607f68',
    federatedIdentityProvider: 'cognito-idp.us-west-2.amazonaws.com/us-west-2_mQk5uSu7r'
};

// API Gateway Configuration
const API_CONFIG = {
    baseUrl: 'https://bivy4b50qa.execute-api.us-west-2.amazonaws.com/dev',
    endpoints: {
        generate: '/generate',
        checkFile: '/generate/check-file',
        progress: '/progress',
        files: '/files',
        upload: '/upload'
    }
};

const calculateEstimatedTime = (files) => {
    const BASE_TIME = 150000;  // Base processing time in ms
    const CHAR_FACTOR = 0.1;   // Time per character
    const FILE_FACTOR = 20000; // Time per file
    
    let totalSize = 0;
    files.forEach(file => {
        totalSize += file.size;
    });
    
    return BASE_TIME + (totalSize * CHAR_FACTOR) + (files.length * FILE_FACTOR);
};

const DisclaimerBanner = () => {
  return (
    <div className="disclaimer-banner">
      <div className="disclaimer-icon">⚠️</div>
      <div className="disclaimer-content">
        <h4>Important Disclaimer</h4>
        <p>
          This tool uses AI to generate threat models as a starting point. As the user of this tool, 
          you are responsible for validating, adding, or removing any data generated. 
          The final output requires human verification to ensure accuracy and completeness.
        </p>
      </div>
    </div>
  );
};

const UploadTest = () => {
    const [useGoogleData, setUseGoogleData] = useState(true);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [productName, setProductName] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isComplete, setIsComplete] = useState(false);
    const [documentUrl, setDocumentUrl] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [awsCredentials, setAwsCredentials] = useState('null');
    const [estimatedTime, setEstimatedTime] = useState('');
    const [processingStatus, setProcessingStatus] = useState('');
    const [downloadReady, setDownloadReady] = useState(false);
    const { width, height } = useWindowSize();
    const [showConfetti, setShowConfetti] = useState(false);
    const [progressCheckInterval, setProgressCheckInterval] = useState(null);
    
    // Initialize progress data with any stored value from session storage
    const [progressData, setProgressData] = useState(() => {
        const storedProgress = sessionStorage.getItem('lastProgressPercent');
        const storedRequestId = sessionStorage.getItem('lastRequestId');
        const storedProductName = sessionStorage.getItem('lastProductName');
        
        if (storedProgress && storedRequestId) {
            return {
                progress: parseFloat(storedProgress),
                requestId: storedRequestId,
                productName: storedProductName || '',
                message: 'Continuing processing...',
                status: 'processing'
            };
        }
        return null;
    });

    // Initialize product name from stored value if available
    useEffect(() => {
        const storedProductName = sessionStorage.getItem('lastProductName');
        if (storedProductName && !productName) {
            setProductName(storedProductName);
        }
    }, [productName]);
    
    // Check if we need to resume progress monitoring for an in-progress job
    useEffect(() => {
        const storedRequestId = sessionStorage.getItem('lastRequestId');
        
        if (storedRequestId && (!progressData || progressData.status === 'processing')) {
            console.log(`Starting progress monitoring for request: ${storedRequestId}`);
            const cleanup = startProgressChecking(storedRequestId);
            return cleanup;
        }
    }, []);  // Empty dependency array - only run on mount

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const error = params.get('error');
        const errorDescription = params.get('error_description');

        if (code) {
            console.log('Authorization code received');
            setIsAuthenticated(true);
            sessionStorage.setItem('isAuthenticated', 'true');
            window.history.replaceState({}, document.title, window.location.pathname);
            getAwsCredentials(code);
            // Don't set isLoading to false if we have an in-progress job
            if (!progressData || progressData.status !== 'processing') {
                setIsLoading(false);
            }
        } else if (error) {
            console.error('Auth error:', errorDescription);
            setError('Authentication failed: ' + errorDescription);
            setIsLoading(false);
        } else if (sessionStorage.getItem('isAuthenticated') === 'true') {
            console.log('Already authenticated from session');
            setIsAuthenticated(true);
            // Don't set isLoading to false if we have an in-progress job
            if (!progressData || progressData.status !== 'processing') {
                setIsLoading(false);
            }
        } else {
            startAuthFlow();
        }

        return () => {
            if (progressCheckInterval) {
                clearInterval(progressCheckInterval);
            }
        };
    }, []);

    // Add this useEffect for cleanup
    useEffect(() => {
        // Return cleanup function
        return () => {
            if (progressCheckInterval) {
                console.log('Cleaning up progress check interval on unmount');
                clearInterval(progressCheckInterval);
                setProgressCheckInterval(null);
            }
        };
    }, [progressCheckInterval]);

    const startAuthFlow = () => {
        const authUrl = 'https://' + AUTH_CONFIG.cognitoDomain + '/oauth2/authorize' +
            '?client_id=' + AUTH_CONFIG.clientId +
            '&response_type=code' +
            '&scope=' + encodeURIComponent('openid email profile') +
            '&redirect_uri=' + encodeURIComponent(AUTH_CONFIG.redirectUri) +
            '&identity_provider=' + AUTH_CONFIG.identityProvider;

        console.log('Starting auth flow');
        window.location.href = authUrl;
    };

    const exchangeCodeForTokens = async (code) => {
        try {
            const tokenEndpoint = 'https://' + AUTH_CONFIG.cognitoDomain + '/oauth2/token';
            
            const params = new URLSearchParams();
            params.append('grant_type', 'authorization_code');
            params.append('client_id', AUTH_CONFIG.clientId);
            params.append('code', code);
            params.append('redirect_uri', AUTH_CONFIG.redirectUri);
    
            const response = await fetch(tokenEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: params
            });
    
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Token exchange failed');
            }
    
            // Store the tokens
            sessionStorage.setItem('idToken', data.id_token);
            
            // Decode the ID token to get user info
            const tokenPayload = JSON.parse(atob(data.id_token.split('.')[1]));
            
            // Store user info in session storage
            const userInfo = {
                name: tokenPayload.name || tokenPayload.preferred_username || tokenPayload.email?.split('@')[0],
                email: tokenPayload.email,
                sub: tokenPayload.sub,
                identityProvider: tokenPayload.iss
            };
            sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
            
            return data.id_token;
        } catch (error) {
            console.error('Token exchange error:', error);
            throw error;
        }
    };

    const getAwsCredentials = async (code) => {
        try {
            console.log('Getting AWS credentials...');
            const idToken = await exchangeCodeForTokens(code);
            console.log('ID Token received');
                
            const identityClient = new CognitoIdentityClient({ region: AWS_CONFIG.region });
                
            const logins = {
                [AWS_CONFIG.federatedIdentityProvider]: idToken
            };
                
            const { IdentityId } = await identityClient.send(new GetIdCommand({
                IdentityPoolId: AWS_CONFIG.identityPoolId,
                Logins: logins
            }));
            
            // Store the identity ID for later use
            sessionStorage.setItem('identityId', IdentityId);
            
            const { Credentials } = await identityClient.send(new GetCredentialsForIdentityCommand({
                IdentityId,
                Logins: logins
            }));
                
            console.log('Got AWS credentials');
            setAwsCredentials(Credentials);
    
        } catch (error) {
            console.error('Error getting AWS credentials:', error);
            throw error;
        }
    };

    const uploadToS3 = async (file) => {
        if (!awsCredentials) {
            throw new Error('No AWS credentials available');
        }
    
        console.log('Starting S3 upload...');
        setProcessingStatus('Uploading file...');
    
        const s3Client = new S3Client({
            region: AWS_CONFIG.region,
            credentials: {
                accessKeyId: awsCredentials.AccessKeyId,
                secretAccessKey: awsCredentials.SecretKey,
                sessionToken: awsCredentials.SessionToken
            }
        });
    
        const timestamp = Math.floor(Date.now() / 1000); // Convert to seconds
        const safeProductName = productName.replace(/[^a-zA-Z0-9-]/g, '_');
        const key = 'uploads/' + safeProductName + '-' + timestamp + '-' + file.name;
    
        try {
            // Configuration for multipart upload
            const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks
            const fileSize = file.size;
            const numParts = Math.ceil(fileSize / CHUNK_SIZE);
    
            if (fileSize > 5 * 1024 * 1024) { // Use multipart upload for files larger than 5MB
                // Initialize multipart upload
                const multipartParams = {
                    Bucket: 'ai-threatmodel-documents-dev',
                    Key: key,
                    ContentType: file.type
                };
    
                const { UploadId } = await s3Client.send(new CreateMultipartUploadCommand(multipartParams));
                console.log('Initiated multipart upload:', UploadId);
    
                // Upload parts
                const uploadPromises = [];
                const parts = [];
    
                for (let i = 0; i < numParts; i++) {
                    const start = i * CHUNK_SIZE;
                    const end = Math.min(start + CHUNK_SIZE, fileSize);
                    const chunk = await file.slice(start, end).arrayBuffer();
    
                    const uploadPartParams = {
                        Bucket: 'ai-threatmodel-documents-dev',
                        Key: key,
                        UploadId: UploadId,
                        PartNumber: i + 1,
                        Body: chunk
                    };
    
                    const partPromise = s3Client.send(new UploadPartCommand(uploadPartParams))
                        .then(response => {
                            parts.push({
                                PartNumber: i + 1,
                                ETag: response.ETag
                            });
                            // Update upload progress
                            const progress = ((i + 1) / numParts) * 100;
                            setProcessingStatus(`Uploading: ${Math.round(progress)}%`);
                        });
    
                    uploadPromises.push(partPromise);
                }
    
                // Wait for all parts to upload
                await Promise.all(uploadPromises);
    
                // Complete multipart upload
                const completeParams = {
                    Bucket: 'ai-threatmodel-documents-dev',
                    Key: key,
                    UploadId: UploadId,
                    MultipartUpload: {
                        Parts: parts.sort((a, b) => a.PartNumber - b.PartNumber)
                    }
                };
    
                await s3Client.send(new CompleteMultipartUploadCommand(completeParams));
                console.log('Completed multipart upload:', key);
    
            } else {
                // Use regular upload for smaller files
                const fileBuffer = await file.arrayBuffer();
                await s3Client.send(new PutObjectCommand({
                    Bucket: 'ai-threatmodel-documents-dev',
                    Key: key,
                    Body: fileBuffer,
                    ContentType: file.type
                }));
            }
    
            console.log('Successfully uploaded to S3:', key);
            return key;
        } catch (error) {
            console.error('S3 upload error:', error);
            // Attempt to abort multipart upload if it exists
            if (error.UploadId) {
                try {
                    await s3Client.send(new AbortMultipartUploadCommand({
                        Bucket: 'ai-threatmodel-documents-dev',
                        Key: key,
                        UploadId: error.UploadId
                    }));
                } catch (abortError) {
                    console.error('Error aborting multipart upload:', abortError);
                }
            }
            throw new Error('Failed to upload file: ' + error.message);
        }
    };

    const handleDownload = () => {
        if (!documentUrl) {
            setError('No document available to download');
            return;
        }
    
        try {
            setProcessingStatus('Starting download...');
    
            // Create S3 client
            const s3Client = new S3Client({
                region: AWS_CONFIG.region,
                credentials: {
                    accessKeyId: awsCredentials.AccessKeyId,
                    secretAccessKey: awsCredentials.SecretKey,
                    sessionToken: awsCredentials.SessionToken
                }
            });
    
            // Normalize the key path based on the S3 URI format
            let key = documentUrl;
            // Remove any 's3://' prefix if present
            if (key.startsWith('s3://')) {
                key = key.substring(5); // Remove "s3://"
                key = key.substring(key.indexOf('/') + 1); // Remove bucket name
            }
            // Ensure key starts with threat-models/ and has proper path structure
            if (!key.startsWith('threat-models/')) {
                key = `threat-models/${key.replace('threat-models', '')}`;
            }
            // Clean up any double slashes
            key = key.replace(/\/+/g, '/');
    
            console.log("S3 URI format:", `s3://ai.finalreport/${key}`);
            console.log("Attempting download with key:", key);
    
            const command = new GetObjectCommand({
                Bucket: 'ai.finalreport',
                Key: key
            });
    
            s3Client.send(command).then(async (response) => {
                console.log("S3 download successful");
                const bytes = await response.Body.transformToByteArray();
                const blob = new Blob([bytes], { 
                    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
                });
                
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                // Use the original filename from the S3 key
                const fileName = key.split('/').pop();
                a.download = fileName || `${productName}_ThreatModel.docx`;
                
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                
                setProcessingStatus('Download complete!');
            }).catch(error => {
                console.error('S3 download error:', error);
                console.error('Attempted key:', key);
                console.error('Attempted bucket:', 'ai.finalreport');
                setError(`Failed to download: ${error.message}`);
            });
    
        } catch (error) {
            console.error('Download function error:', error);
            setError(`Download error: ${error.message}`);
        }
    };

    // New function that uses API Gateway instead of direct Lambda invocation
    const invokeGenerateThreatModel = async (s3Keys, maxRetries = 3) => {
        if (!awsCredentials) {
            throw new Error('No AWS credentials available');
        }
    
        console.log('Invoking threat model generation via API Gateway...');
        setProcessingStatus('Generating threat model...');
    
        // Enhanced user identity extraction
        let userInfo;
        const idToken = sessionStorage.getItem('idToken');
        console.log('Retrieved ID token:', idToken ? 'Present' : 'Not found');
    
        try {
            if (idToken) {
                // Parse and decode the JWT token
                const tokenParts = idToken.split('.');
                const payload = JSON.parse(atob(tokenParts[1]));
                console.log('Token payload:', payload);
    
                // Extract user information with multiple fallbacks
                const name = payload.name || 
                            payload.preferred_username || 
                            payload.email?.split('@')[0] ||
                            payload['cognito:username'];
    
                userInfo = {
                    cognitoId: payload.sub || payload.user_id,
                    username: name,
                    email: payload.email || 'no-email@example.com',
                    userAgent: navigator.userAgent,
                    given_name: payload.given_name || null,
                    family_name: payload.family_name || null,
                    idpProvider: payload.iss?.includes('google') ? 'Google' : 
                               payload.iss?.includes('azure') ? 'Azure' : 
                               'Cognito'
                };
    
                console.log('Extracted user info:', userInfo);
            } else {
                console.log('No ID token found, using anonymous user');
                userInfo = {
                    cognitoId: 'Anonymous',
                    username: 'Anonymous',
                    email: 'no-email@example.com',
                    userAgent: navigator.userAgent
                };
            }
        } catch (error) {
            console.warn('Error extracting user info:', error);
            userInfo = {
                cognitoId: 'Anonymous',
                username: 'Anonymous',
                email: 'no-email@example.com',
                userAgent: navigator.userAgent
            };
        }
    
        const apiEndpoint = API_CONFIG.baseUrl + API_CONFIG.endpoints.generate;
        
        // Create request ID for tracking
        const timestamp = Math.floor(Date.now() / 1000); // Convert to seconds
        const requestId = `${productName}-${timestamp}`; 
        console.log(`Generated request ID: ${requestId}`);
        
        try {
            const response = await fetch(apiEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': idToken ? `Bearer ${idToken}` : undefined
                },
                mode: 'cors',
                body: JSON.stringify({
                    productName: productName,
                    files: s3Keys,
                    useGoogleData: useGoogleData,
                    userInfo: userInfo,
                    requestId: requestId // Send the request ID to backend
                })
            });
            
            let data;
            try {
                data = await response.json();
                console.log('API response:', data);
            } catch (e) {
                console.error('Failed to parse API response:', e);
                // Create a fallback response
                data = {
                    requestId: requestId, 
                    status: "accepted",
                    message: "Processing started in background (parsing error)"
                };
            }
            
            // Start progress checking with our request ID ONLY if successful
            if (response.ok) {
                startProgressChecking(data.requestId || requestId);
                return data;
            } else {
                console.error(`API request failed with status ${response.status}`);
                
                // Don't start progress checking for failed requests
                setIsLoading(false);
                
                // Show error message from API if available
                const errorMessage = data?.error || data?.message || `Request failed with status ${response.status}`;
                const errorDetails = data?.details ? `\n\nDetails:\n${data.details.map(d => `- ${d.path}: ${d.reason}`).join('\n')}` : '';
                
                setError(errorMessage + errorDetails);
                
                throw new Error(errorMessage);
            }
        } catch (error) {
            console.error('API request error:', error);
            
            // Start progress checking even if API call fails
            startProgressChecking(requestId);
            
            // Return response with our request ID so UI can continue
            return {
                requestId: requestId,
                status: "accepted",
                message: "Processing started in background (connection error)"
            };
        }
    };

    const startProgressChecking = (requestId) => {
        // Clear any existing interval first
        if (progressCheckInterval) {
            clearInterval(progressCheckInterval);
        }
    
        // Initialize tracking variables
        let lastUpdateTime = Date.now();
        let consecutiveNoChanges = 0;
        const MAX_NO_CHANGES = 20;
        let currentInterval = null;
    
        // Function to perform progress check
        const performProgressCheck = async () => {
            try {
                // Check if already complete
                if (progressData?.status === 'complete') {
                    if (currentInterval) {
                        clearInterval(currentInterval);
                        setProgressCheckInterval(null);
                    }
                    return;
                }
    
                console.log(`Checking progress for ${requestId}`);
                const progressEndpoint = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.progress}?requestId=${requestId}&productName=${encodeURIComponent(productName)}`;
                
                const response = await fetch(progressEndpoint, {
                    method: 'GET',
                    headers: {
                        'Authorization': sessionStorage.getItem('idToken') ? `Bearer ${sessionStorage.getItem('idToken')}` : ''
                    }
                });
    
                // Check for non-200 responses and handle them
                if (!response.ok) {
                    console.error(`Progress API returned error status: ${response.status}`);
                    try {
                        const errorText = await response.text();
                        console.error('Error response:', errorText);
                    } catch (e) {
                        console.error('Could not read error response text');
                    }
                    return;
                }
    
                let data;
                try {
                    data = await response.json();
                } catch (parseError) {
                    console.error('Error parsing JSON response:', parseError);
                    try {
                        const textResponse = await response.text();
                        console.error('Raw response:', textResponse);
                    } catch (textError) {
                        console.error('Could not get response text:', textError);
                    }
                    
                    // Continue checking despite parse error
                    return;
                }
    
                if (data.status === 'not_found') {
                    console.log('Job not found yet, continuing checks');
                    return;
                }
    
                // Handle PII detection with client action
                if (data.status === 'error' && (
                    data.clientAction === 'RETURN_TO_UPLOAD' || 
                    (data.message && data.message.includes('PII detected'))
                )) {
                    console.log('PII detected, returning to upload page');
                    
                    // Extract PII details if available
                    const piiFile = data.piiFile || 'uploaded file';
                    
                    // Store the error message to display on the form
                    setError(data.message || `PII detected in ${piiFile}. Please remove PII before uploading.`);
                    
                    // Reset loading state to show the form
                    setIsLoading(false);
                    
                    // Clear progress data to reset the UI
                    setProgressData(null);
                    
                    // Clean up session storage
                    sessionStorage.removeItem('lastProgressPercent');
                    sessionStorage.removeItem('lastRequestId');
                    
                    // Stop the progress checks
                    if (currentInterval) {
                        clearInterval(currentInterval);
                        setProgressCheckInterval(null);
                    }
                    
                    return;
                }
    
                // Check if progress has actually changed
                const hasProgressChanged = 
                    !progressData || 
                    data.progress > progressData.progress || 
                    data.status !== progressData.status ||
                    data.message !== progressData.message;
    
                if (hasProgressChanged) {
                    console.log('Progress changed, updating UI:', data);
                    
                    // Save progress in sessionStorage
                    if (data.progress) {
                        sessionStorage.setItem('lastProgressPercent', data.progress.toString());
                        sessionStorage.setItem('lastRequestId', requestId);
                        if (data.productName) {
                            sessionStorage.setItem('lastProductName', data.productName);
                        }
                    }
    
                    // Update progress state
                    setProgressData(data);
    
                    // Update status message
                    if (data.progress !== undefined) {
                        const progressPercent = Math.round(data.progress * 100);
                        setProcessingStatus(data.message || `Processing: ${progressPercent}%`);
                    }
    
                    // Reset stall detection on progress
                    lastUpdateTime = Date.now();
                    consecutiveNoChanges = 0;
                } else {
                    console.log('No significant progress change');
                    consecutiveNoChanges++;
    
                    // Check for stalled progress
                    const currentTime = Date.now();
                    if (consecutiveNoChanges >= MAX_NO_CHANGES) {
                        const timeSinceUpdate = currentTime - lastUpdateTime;
                        console.log(`No progress changes for ${timeSinceUpdate/1000}s`);
                        
                        // If stalled for over 5 minutes, slow down checks
                        if (timeSinceUpdate > 300000) {
                            console.log('Switching to slower polling interval');
                            if (currentInterval) {
                                clearInterval(currentInterval);
                                currentInterval = setInterval(performProgressCheck, 10000);
                                setProgressCheckInterval(currentInterval);
                            }
                        }
                    }
                }
    
                // Handle completion
                if (data.status === 'complete' && data.outputKey) {
                    // Only trigger confetti on first completion
                    if (!isComplete) {
                        setShowConfetti(true);
                        setTimeout(() => setShowConfetti(false), 5000);
                    }
    
                    setDocumentUrl(data.outputKey);
                    setDownloadReady(true);
                    setIsComplete(true);
                    setProcessingStatus('Complete! Click to download.');
    
                    // Clean up storage and interval
                    sessionStorage.removeItem('lastProgressPercent');
                    sessionStorage.removeItem('lastRequestId');
    
                    if (currentInterval) {
                        clearInterval(currentInterval);
                        setProgressCheckInterval(null);
                    }
                }
    
            } catch (error) {
                console.error('Error in progress check:', error);
                consecutiveNoChanges++;
    
                // Switch to slower polling on repeated errors
                if (consecutiveNoChanges > MAX_NO_CHANGES) {
                    if (currentInterval) {
                        clearInterval(currentInterval);
                        currentInterval = setInterval(performProgressCheck, 10000);
                        setProgressCheckInterval(currentInterval);
                    }
                }
            }
        };
    
        // Do immediate first check
        performProgressCheck();
    
        // Start regular checking
        currentInterval = setInterval(performProgressCheck, 3000);
        setProgressCheckInterval(currentInterval);
    
        // Add cleanup handlers
        const cleanup = () => {
            if (currentInterval) {
                clearInterval(currentInterval);
                setProgressCheckInterval(null);
            }
        };
    
        // Cleanup on page unload
        window.addEventListener('beforeunload', cleanup);
    
        // Safety timeout after 30 minutes
        const safetyTimeout = setTimeout(() => {
            if (currentInterval) {
                console.log('Safety timeout reached');
                cleanup();
                
                if (!isComplete) {
                    setProcessingStatus('Processing is taking longer than expected. Please check back later.');
                    setError('Processing timeout reached. The process will continue in the background.');
                }
            }
        }, 30 * 60 * 1000);
    
        // Return cleanup function for useEffect
        return () => {
            cleanup();
            clearTimeout(safetyTimeout);
            window.removeEventListener('beforeunload', cleanup);
        };
    };
    

    const checkProgress = async (requestId) => {
        try {
            console.log(`Checking progress for ${requestId}`);
            const progressEndpoint = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.progress}?requestId=${requestId}&productName=${encodeURIComponent(productName)}`;
            
            const response = await fetch(progressEndpoint, {
                method: 'GET',
                headers: {
                    'Authorization': sessionStorage.getItem('idToken') || ''
                }
            });
            
            try {
                const data = await response.json();
                
                if (data.status === 'not_found') {
                    console.log('Job not found yet, continuing with simulation');
                    simulateProgressUpdate(requestId);
                    return;
                }
                
                // Handle PII error with client action to return to upload
                if (data.status === 'error' && data.clientAction === 'RETURN_TO_UPLOAD') {
                    console.log('PII detected, returning to upload page');
                    
                    // Extract PII details if available
                    const piiFile = data.piiFile || 'uploaded file';
                    
                    // Store the error message to display on the form
                    setError(data.message || `PII detected in ${piiFile}. Please remove PII before uploading.`);
                    
                    // Reset loading state to show the form
                    setIsLoading(false);
                    
                    // Clear progress data to reset the UI
                    setProgressData(null);
                    
                    // Clean up session storage
                    sessionStorage.removeItem('lastProgressPercent');
                    sessionStorage.removeItem('lastRequestId');
                    
                    // Stop the progress checks
                    if (progressCheckInterval) {
                        console.log('Detected PII, stopping progress checks');
                        clearInterval(progressCheckInterval);
                        setProgressCheckInterval(null);
                    }
                    
                    return;
                }
                
                // Only update state if progress has actually changed
                const hasProgressChanged = 
                    !progressData || 
                    data.progress > progressData.progress || 
                    data.status !== progressData.status;
                    
                if (hasProgressChanged) {
                    console.log('Progress changed, updating UI:', data);
                    
                    // Save progress and request info in sessionStorage
                    if (data.progress) {
                        sessionStorage.setItem('lastProgressPercent', data.progress);
                        sessionStorage.setItem('lastRequestId', requestId);
                        if (data.productName) {
                            sessionStorage.setItem('lastProductName', data.productName);
                        }
                    }
                    
                    // Update progress data state
                    setProgressData(data);
                    
                    // Update progress percentage if available
                    if (data.progress !== undefined) {
                        const progressPercent = Math.round(data.progress * 100);
                        setProcessingStatus(data.message || `Processing: ${progressPercent}%`);
                    }
                } else {
                    console.log('No significant progress change, skipping UI update');
                }
                
                // Handle completion regardless of whether progress changed
                if (data.status === 'complete' && data.outputKey) {
                    // Only trigger confetti if this is the first time we've seen completion
                    if (!isComplete) {
                        setShowConfetti(true);
                        setTimeout(() => setShowConfetti(false), 5000);
                    }
                    
                    setDocumentUrl(data.outputKey);
                    setDownloadReady(true);
                    setIsComplete(true);
                    setProcessingStatus('Complete! Click to download.');
                    
                    // Clean up session storage for completed jobs
                    sessionStorage.removeItem('lastProgressPercent');
                    sessionStorage.removeItem('lastRequestId');
                    
                    // Stop the progress checks
                    if (progressCheckInterval) {
                        console.log('Detected completion, stopping progress checks');
                        clearInterval(progressCheckInterval);
                        setProgressCheckInterval(null);
                    }
                    return;
                }
                
                // Handle other error states
                if (data.status === 'error' && !data.clientAction) {
                    console.log('Error status detected:', data.message);
                    setError(data.message || 'An error occurred during processing');
                    
                    // Don't stop checking progress completely, but reduce frequency
                    if (progressCheckInterval) {
                        clearInterval(progressCheckInterval);
                        setProgressCheckInterval(setInterval(() => checkProgress(requestId), 10000)); // Check every 10 seconds
                    }
                }
                
            } catch (error) {
                console.error('Error parsing response JSON:', error);
                
                // Log the raw response for debugging
                try {
                    const responseText = await response.text();
                    console.error('Response text:', responseText);
                } catch (e) {
                    console.error('Could not get response text:', e);
                }
                
                simulateProgressUpdate(requestId);
            }
        } catch (error) {
            console.error('Error checking progress:', error);
            simulateProgressUpdate(requestId);
        }
    };
    

    const simulateProgressUpdate = (currentRequestId) => {
        // If no requestId provided, try to use the one from progressData or create a new one
        const requestId = currentRequestId || 
                        (progressData && progressData.requestId) || 
                        `${productName}-${Date.now()}`;

        // Get previous progress or start at 0
        const previousProgress = progressData ? (progressData.progress || 0) : 0;
        
        // Increase progress gradually, cap at 95% until completion
        const newProgress = Math.min(previousProgress + 0.05, 0.95);
        
        const simulatedProgress = {
            requestId: requestId,
            productName: productName,
            status: 'processing',
            progress: newProgress,
            message: 'Processing documents...'
        };
        
        console.log('Simulated progress data:', simulatedProgress);
        setProgressData(simulatedProgress);
        
        // Show accurate percentage without time estimates
        const progressPercent = Math.round(newProgress * 100);
        setProcessingStatus(`Processing: ${progressPercent}%`);
        
        // After 2 minutes (120000 ms), simulate completion
        const timeStampPart = requestId.split('-')[1];
        if (timeStampPart && Date.now() - parseInt(timeStampPart) > 120000) {
            simulatedProgress.status = 'complete';
            simulatedProgress.progress = 1.0;
            simulatedProgress.message = 'Processing complete!';
            simulatedProgress.outputKey = `threat-models/${productName.replace(/[^a-zA-Z0-9]/g, '_')}_threat_model.docx`;
            
            setDocumentUrl(simulatedProgress.outputKey);
            setDownloadReady(true);
            setIsComplete(true);
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 5000);
            setProcessingStatus('Complete! Click to download.');
            
            if (progressCheckInterval) {
                clearInterval(progressCheckInterval);
                setProgressCheckInterval(null);
            }
        }
    };

    const ProgressIndicator = ({ progress, status }) => {
        const percent = Math.round((progress || 0) * 100);
        
        // Animated progress bar for smoother user experience
        const [displayPercent, setDisplayPercent] = useState(0);
        
        // Update the displayed percentage smoothly
        useEffect(() => {
            if (displayPercent < percent) {
                const timer = setTimeout(() => {
                    setDisplayPercent(prev => Math.min(prev + 1, percent));
                }, 30);
                return () => clearTimeout(timer);
            }
        }, [displayPercent, percent]);
        
        return (
            <div className="progress-container">
                <div className="progress-bar">
                    <div 
                        className="progress-fill" 
                        style={{ width: `${displayPercent}%` }}
                    ></div>
                </div>
                <div className="progress-text">
                    <span className="progress-percentage">{displayPercent}%</span>
                    <span className="progress-status">{status || "Processing..."}</span>
                </div>
            </div>
        );
    };
    
    
    const ProgressTimeline = ({ currentProgress }) => {
        const stages = [
            { name: "Initialization", progress: 0.1, icon: "🚀" },
            { name: "Document Analysis", progress: 0.2, icon: "📄" },
            { name: "Content Generation", progress: 0.6, icon: "✍️" },
            { name: "Security Analysis", progress: 0.9, icon: "🔒" },
            { name: "Finalization", progress: 1.0, icon: "✅" }
        ];
        
        return (
            <div className="progress-timeline">
                {stages.map((stage, index) => (
                    <div 
                        key={index} 
                        className={`timeline-stage ${currentProgress >= stage.progress ? 'completed' : ''}`}
                    >
                        <span className="stage-icon">{stage.icon}</span>
                        <span className="stage-name">{stage.name}</span>
                    </div>
                ))}
            </div>
        );
    };

    const handleAddFiles = (event) => {
        const newFiles = Array.from(event.target.files);
        
        // Filter only PDF files
        const pdfFiles = newFiles.filter(file => {
            const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
            if (!isPdf) {
                console.warn(`Rejected non-PDF file: ${file.name}`);
            }
            return isPdf;
        });
        
        // Show error if any files were rejected
        if (pdfFiles.length < newFiles.length) {
            const rejectedCount = newFiles.length - pdfFiles.length;
            setError(`${rejectedCount} file(s) rejected. Only PDF files are accepted.`);
            setTimeout(() => setError(null), 5000); // Clear error after 5 seconds
        }
        
        // Only add PDF files
        if (pdfFiles.length > 0) {
            setSelectedFiles(prev => {
                const updatedFiles = [...prev, ...pdfFiles];
                const estimatedMs = calculateEstimatedTime(updatedFiles);
                setEstimatedTime(estimatedMs);
                return updatedFiles;
            });
        }
    };

    const handleRemoveFile = (indexToRemove) => {
        setSelectedFiles(prev => {
            const updatedFiles = prev.filter((_, index) => index !== indexToRemove);
            if (updatedFiles.length > 0) {
                const estimatedMs = calculateEstimatedTime(updatedFiles);
                setEstimatedTime(estimatedMs);
            } else {
                setEstimatedTime('');
            }
            return updatedFiles;
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        setError(null);
        setIsComplete(false);
        setDocumentUrl(null);
        setDownloadReady(false);
        
        if (selectedFiles.length === 0 || !productName) {
            setError('Please select at least one file and enter a product name');
            setIsLoading(false);
            return;
        }
        
        try {
            // Initialize progress data immediately to show UI
            setProgressData({
                requestId: `${productName}-${Date.now()}`,
                progress: 0.05,
                status: 'processing',
                message: 'Starting processing...'
            });
            
            setProcessingStatus('Starting processing...');
            
            // Upload all files and get their S3 keys
            const s3Keys = await Promise.all(
                selectedFiles.map(file => uploadToS3(file))
            );
            
            // Pass all S3 keys to API Gateway
            const result = await invokeGenerateThreatModel(s3Keys);
            
            // Update with actual requestId after API call
            if (result && result.requestId) {
                sessionStorage.setItem('lastRequestId', result.requestId);
                sessionStorage.setItem('lastProductName', productName);
                sessionStorage.setItem('lastProgressPercent', '0.05'); // Initial progress
                
                // Update progress data with actual request ID
                setProgressData(prev => ({
                    ...prev,
                    requestId: result.requestId
                }));
            }
            
        } catch (error) {
            setError(error.message);
            console.error('Submission error:', error);
            setProcessingStatus('');
            setIsLoading(false);
            setProgressData(null); // Clear progress data on error
        }
    };

    const formatTime = (ms) => {
        if (!ms) return '0 seconds';
        
        const minutes = Math.floor(ms / 60000);
        const seconds = Math.floor((ms % 60000) / 1000);
        
        if (minutes > 0) {
            return `${minutes} minute${minutes > 1 ? 's' : ''} and ${seconds} second${seconds !== 1 ? 's' : ''}`;
        }
        return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    };

    const resetForm = () => {
        // Don't refresh the page, just reset the state
        setSelectedFiles([]);
        setEstimatedTime('');
        setError(null);
        setProgressData(null);
        setIsComplete(false);
        setDownloadReady(false);
        setDocumentUrl('');
        setProcessingStatus('');
        setShowConfetti(false);
        setIsLoading(false);
        
        // Clear all stored progress data
        sessionStorage.removeItem('lastProgressPercent');
        sessionStorage.removeItem('lastRequestId');
// sessionStorage.removeItem('lastProductName'); // Optional: keep product name if desired
        
        // Stop any existing progress checks
        if (progressCheckInterval) {
            clearInterval(progressCheckInterval);
            setProgressCheckInterval(null);
        }
        
        // Clear product name if desired
        setProductName('');
        
        console.log('Form has been reset, all progress data cleared');
    };

    const LoadingScreen = () => {
        // Use animated progress display with better initialization
        const [displayPercent, setDisplayPercent] = useState(
            progressData ? Math.round(progressData.progress * 100) : 5
        );
        
        // Get the actual progress percentage with a default of 5%
        const progressPercent = progressData ? Math.round(progressData.progress * 100) : 5;
        
        // Store the highest progress value to prevent going backwards
        const [highestProgress, setHighestProgress] = useState(progressPercent);
        
        // Update highest progress if current progress is higher
        useEffect(() => {
            if (progressPercent > highestProgress) {
                setHighestProgress(progressPercent);
                
                // Only animate when actual progress increases
                animateToTarget(progressPercent);
            }
        }, [progressPercent, highestProgress]);
        
        // Function to animate progress smoothly
        const animateToTarget = (target) => {
            // Start from current display percent
            let current = displayPercent;
            
            // Clear any existing animation
            if (window.progressAnimation) {
                clearInterval(window.progressAnimation);
            }
            
            // Create new animation
            window.progressAnimation = setInterval(() => {
                if (current >= target) {
                    clearInterval(window.progressAnimation);
                    return;
                }
                
                current += 1;
                setDisplayPercent(current);
                
                if (current >= target) {
                    clearInterval(window.progressAnimation);
                }
            }, 30);
        };
        
        // Cleanup animation on unmount
        useEffect(() => {
            return () => {
                if (window.progressAnimation) {
                    clearInterval(window.progressAnimation);
                }
            };
        }, []);
        
        // Get current phase based on progress
        const getCurrentPhase = () => {
            // Use the higher of current progress or display progress
            const progress = Math.max(
                progressData?.progress || 0.05, 
                highestProgress / 100
            );
            
            if (progress >= 0.95) return 4;  // Finalization
            if (progress >= 0.85) return 3;  // Security Review
            if (progress >= 0.25) return 2;  // Content Generation
            if (progress >= 0.10) return 1;  // Analysis
            return 0;  // Initialization
        };
        
        const currentPhase = getCurrentPhase();
        
        return (
            <div className="loading-container-new">
                {progressData && (
                    <div className="loading-progress">
                        <h3 className="processing-title">
                            Creating Your Threat Model
                        </h3>
                        
                        {/* Enhanced progress indicator */}
                        <div className="progress-container">
                            <div className="progress-bar">
                                <div 
                                    className="progress-fill"
                                    style={{ width: `${displayPercent}%` }}
                                >
                                    <div className="progress-glow"></div>
                                </div>
                            </div>
                            <div className="progress-text">
                                <div className="progress-percentage-container">
                                    <span className="progress-percentage">{displayPercent}%</span>
                                    <span className="progress-status">{progressData.message || 'Processing...'}</span>
                                </div>
                            </div>
                            
                            {/* Sub-progress bar for detailed operations */}
                            {progressData.subProgress && (
                                <div className="sub-progress-container">
                                    <div className="sub-progress-bar">
                                        <div 
                                            className="sub-progress-fill"
                                            style={{ width: `${Math.round(progressData.subProgress.percentage * 100)}%` }}
                                        />
                                    </div>
                                    <div className="sub-progress-text">
                                        <span className="sub-progress-message">
                                            {progressData.subProgress.message}
                                        </span>
                                        <span className="sub-progress-count">
                                            ({progressData.subProgress.current}/{progressData.subProgress.total})
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Phase indicators */}
                        <div className="processing-phases">
                            <div className={`phase ${currentPhase >= 0 ? 'active' : ''} ${currentPhase > 0 ? 'completed' : ''}`}>
                                <span className="phase-icon">🚀</span>
                                <span className="phase-name">Initialization</span>
                            </div>
                            <div className={`phase ${currentPhase >= 1 ? 'active' : ''} ${currentPhase > 1 ? 'completed' : ''}`}>
                                <span className="phase-icon">📄</span>
                                <span className="phase-name">Analysis</span>
                            </div>
                            <div className={`phase ${currentPhase >= 2 ? 'active' : ''} ${currentPhase > 2 ? 'completed' : ''}`}>
                                <span className="phase-icon">📝</span>
                                <span className="phase-name">Content</span>
                            </div>
                            <div className={`phase ${currentPhase >= 3 ? 'active' : ''} ${currentPhase > 3 ? 'completed' : ''}`}>
                                <span className="phase-icon">🔒</span>
                                <span className="phase-name">Security</span>
                            </div>
                            <div className={`phase ${currentPhase >= 4 ? 'active' : ''}`}>
                                <span className="phase-icon">✅</span>
                                <span className="phase-name">Completion</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };
    

    if (isLoading && !isComplete) {
        return (
            <div className="page-layout">
                <div className="main-content">
                    <div className="upload-container">
                        <LoadingScreen />
                    </div>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="page-layout">
                <div className="main-content">
                    <div className="upload-container">
                        <h2>Authentication Required</h2>
                        <p>Please sign in with your Amazon credentials</p>
                        <button onClick={startAuthFlow}>Sign In with Midway</button>
                        {error && <div className="error-message">{error}</div>}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-layout">
            {showConfetti && (
                <Confetti
                    width={width}
                    height={height}
                    numberOfPieces={200}
                    recycle={false}
                    colors={['#28a745', '#17a2b8', '#ffc107', '#dc3545', '#6610f2']}
                />
            )}
            <div className="main-content">
                {/* Add the disclaimer banner here */}
                {!isLoading && !isComplete && <DisclaimerBanner />}
                
                <div className="upload-container">
                    <div className="brand-header">
                        <img src={threatforgeLogo} alt="ThreatForge Logo" className="brand-logo" />
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="input-group">
                            <input 
                                type="text" 
                                id="productName" 
                                value={productName} 
                                onChange={(e) => setProductName(e.target.value)} 
                                disabled={isLoading || isComplete} 
                                placeholder="Enter product name"
                            />
                        </div>
                        <div className="input-group">
                            <label htmlFor="file">Select PDFs:</label>
                            <input 
                                type="file" 
                                id="file" 
                                accept=".pdf" 
                                onChange={handleAddFiles} 
                                disabled={isLoading || isComplete} 
                                multiple
                            />
                            <small style={{ color: '#666', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>
                                📄 Only PDF files are accepted. Other file types will be rejected.
                            </small>
                            {selectedFiles.length > 0 && (
                                <div className="selected-files">
                                    <p>Selected files ({selectedFiles.length}):</p>
                                    <ul className="file-list">
                                        {selectedFiles.map((file, index) => (
                                            <li key={index}>
                                                <span className="file-name">{file.name}</span>
                                                <button 
                                                    type="button" 
                                                    onClick={() => handleRemoveFile(index)}
                                                    className="remove-file-btn"
                                                    disabled={isLoading || isComplete}
                                                >
                                                    ✕
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                    <button 
                                        type="button"
                                        onClick={() => setSelectedFiles([])}
                                        className="clear-files-btn"
                                        disabled={isLoading || isComplete}
                                    >
                                        Clear All Files
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Only show the Generate button if not complete */}
                        {!isComplete && (
                            <button 
                                type="submit" 
                                disabled={isLoading || selectedFiles.length === 0 || !productName}
                            >
                                {isLoading ? 'Processing...' : 'Generate Threat Model'}
                            </button>
                        )}
                        
                        {/* Show the Reset button if complete - this button only appears in the form section when complete */}
                        {isComplete && (
                            <button 
                                onClick={resetForm}
                                className="reset-button primary-btn"
                                type="button"
                            >
                                Generate New Threat Model
                            </button>
                        )}

                        {error && <div className="error-message">{error}</div>}
                    </form>
                </div>
            </div>

            <div className="right-panel">
                <div className="config-panel">
                    <h3>Analysis Configuration</h3>
                    <div className="toggle-container">
                        <div>
                            <label htmlFor="googleDataToggle">Extended Data Sources</label>
                            <div className="toggle-switch">
                                <input
                                    type="checkbox"
                                    id="googleDataToggle"
                                    checked={useGoogleData}
                                    onChange={(e) => setUseGoogleData(e.target.checked)}
                                    disabled={isLoading || isComplete}
                                />
                                <span className="toggle-slider"></span>
                            </div>
                            <div className="toggle-description">
                                When enabled, the threat model will include additional validated data from Google and other online sources to enhance the analysis.
                                <br/><br/>
                                Note: This may increase generation time but provides more comprehensive results.
                            </div>
                        </div>
                    </div>
                </div>

                {isComplete && documentUrl && (
                    <div className="result-panel">
                        <div className="success-banner">
                            <span className="success-icon">🎉</span>
                            <h2>Success!</h2>
                            <p>Your Threat Model is Ready</p>
                        </div>
                        <button 
                            onClick={handleDownload}
                            className="download-button-large"
                            type="button"
                            disabled={!downloadReady}
                        >
                            <span className="download-icon">📥</span>
                            Download Threat Model
                        </button>
                        <p className="download-info">
                            Your document will be available for 24 hours.
                        </p>
                        <button 
                            onClick={resetForm}
                            className="reset-button"
                            type="button"
                        >
                            Generate New Threat Model
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UploadTest;
